import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useEffect, useState, useMemo } from "react";
import { Html5QrcodeScanner, Html5Qrcode } from "html5-qrcode";
import { useData } from "@/contexts/DataContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Scan, Loader2, Keyboard, Barcode } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import type { Product, ProductType } from "@/lib/googleSheetsService";
import { fetchProductDataZeroCost } from "@/lib/googleSheetsService";

const PRODUCT_COLORS = [
  "#BBDEFB",
  "#C8E6C9",
  "#FFF9C4",
  "#F8BBD9",
  "#FFE0B2",
  "#E1BEE7",
  "#B2DFDB",
  "#FFCCBC",
];

const productFormSchema = z
  .object({
    name: z.string().min(1, "Product name is required"),
    salePrice: z.coerce
      .number()
      .min(0, "Sale price must be 0 or greater")
      .optional(),
    costPrice: z.coerce
      .number()
      .min(0, "Cost price must be 0 or greater")
      .optional(),
    productType: z.enum(["sale", "expense", "both"]).default("both"),
    category: z.string().optional(),
    color: z.string().default(PRODUCT_COLORS[0]),
    barcode: z.string().optional(),
    stock: z.coerce.number().min(0, "Stock must be 0 or greater").optional(),
    lowStockAlert: z.coerce
      .number()
      .min(0, "Low stock alert must be 0 or greater")
      .optional(),
    gstRate: z.coerce
      .number()
      .min(0, "GST rate must be 0 or greater")
      .optional(),
    hsn: z.string().optional(),
    taxType: z.string().default("none"),
    gstInclusive: z.boolean().default(true),
  })
  .superRefine((data, ctx) => {
    if (!data.category || data.category.trim() === "") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Category is required",
        path: ["category"],
      });
    }
    if (data.gstRate === undefined || data.gstRate === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "GST Rate is required",
        path: ["gstRate"],
      });
    }
    if (!data.taxType || data.taxType === "none") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Tax Type is required",
        path: ["taxType"],
      });
    }
    if (!data.hsn || data.hsn.trim() === "") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "HSN Code is required",
        path: ["hsn"],
      });
    }
  });

type ProductFormValues = z.infer<typeof productFormSchema>;

interface AddProductDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: ProductFormValues) => void;
  editProduct?: Product | null;
  isLoading?: boolean;
}

const DEFAULT_CATEGORIES = [
  "General",
  "Food",
  "Electronics",
  "Clothing",
  "Services",
  "Others",
];

export function AddProductDialog({
  open,
  onOpenChange,
  onSubmit,
  editProduct,
  isLoading,
}: AddProductDialogProps) {
  const { toast } = useToast();

  // FIX: Hook called safely at the top level
  const { userMode } = useData();

  const [isScanning, setIsScanning] = useState(false);
  const [scannedQuantity, setScannedQuantity] = useState(1);
  const [isMultiScan, setIsMultiScan] = useState(false);
  const [searchStatus, setSearchStatus] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [dataSource, setDataSource] = useState<string | null>(null);

  const form = useForm<ProductFormValues>({
    resolver: zodResolver(productFormSchema),
    defaultValues: {
      name: "",
      productType: "both",
      salePrice: undefined,
      stock: undefined,
      lowStockAlert: 5,
      gstRate: 0,
      hsn: "",
      taxType: "none",
      gstInclusive: true,
    },
  });

  const productType = form.watch("productType");
  const currentCategory = form.watch("category");

  // FIX: Safely merge incoming unknown categories so the Select component doesn't crash
  const displayCategories = useMemo(() => {
    if (currentCategory && !DEFAULT_CATEGORIES.includes(currentCategory)) {
      return [...DEFAULT_CATEGORIES, currentCategory];
    }
    return DEFAULT_CATEGORIES;
  }, [currentCategory]);

  useEffect(() => {
    if (editProduct) {
      form.reset({
        name: editProduct.name,
        salePrice: editProduct.salePrice,
        costPrice: editProduct.costPrice,
        productType: editProduct.productType,
        category: editProduct.category || "General",
        color: editProduct.color || PRODUCT_COLORS[0],
        barcode: (editProduct as any).barcode || "",
        stock: (editProduct as any).stock ?? undefined,
        lowStockAlert: (editProduct as any).lowStockAlert || 5,
        gstRate: (editProduct as any).gstRate || 0,
        hsn: (editProduct as any).hsn || "",
        taxType: (editProduct as any).taxType || "none",
        gstInclusive: (editProduct as any).gstInclusive ?? true,
      });
    } else {
      form.reset({
        name: "",
        productType: "both",
        salePrice: undefined,
        stock: undefined,
        lowStockAlert: 5,
        gstRate: 0,
        hsn: "",
        taxType: "none",
        gstInclusive: true,
      });
    }
  }, [editProduct, form]);

  useEffect(() => {
    let html5QrCode: Html5Qrcode | null = null;
    const handleScan = async (decodedText: string) => {
      try {
        const audioCtx = new (window.AudioContext ||
          (window as any).webkitAudioContext)();
        const oscillator = audioCtx.createOscillator();
        const gainNode = audioCtx.createGain();
        oscillator.connect(gainNode);
        gainNode.connect(audioCtx.destination);
        oscillator.type = "sine";
        oscillator.frequency.setValueAtTime(880, audioCtx.currentTime);
        gainNode.gain.setValueAtTime(0.1, audioCtx.currentTime);
        oscillator.start();
        oscillator.stop(audioCtx.currentTime + 0.1);
      } catch (e) {
        console.warn("Audio feedback failed", e);
      }

      if ("vibrate" in navigator) {
        navigator.vibrate(50);
      }

      form.setValue("barcode", decodedText);

      try {
        setIsSearching(true);
        setSearchStatus("Checking your shop records...");

        const productInfo = (await fetchProductDataZeroCost(
          decodedText,
        )) as any;

        if (!productInfo) {
          setSearchStatus("Finding Product...");
        }

        if (productInfo) {
          setSearchStatus("Product found!");
          setDataSource(productInfo.source || "external");
          if (productInfo.name && !form.getValues("name")) {
            form.setValue("name", productInfo.name);
          }
          if (
            productInfo.price &&
            productInfo.price > 0 &&
            !form.getValues("salePrice")
          ) {
            form.setValue("salePrice", productInfo.price);
          }
          if (productInfo.category && !form.getValues("category")) {
            const category =
              productInfo.category.charAt(0).toUpperCase() +
              productInfo.category.slice(1);
            form.setValue("category", category);
          }

          if (!isMultiScan) {
            setIsScanning(false);
            setTimeout(() => {
              const priceInput = document.querySelector(
                '[data-testid="input-sale-price"]',
              ) as HTMLInputElement;
              if (priceInput) priceInput.focus();
            }, 300);
            html5QrCode
              ?.stop()
              .catch((err) => console.error("Error stopping scanner", err));
          } else {
            toast({
              title: "Product Found!",
              description: `Added ${productInfo.name || "item"} to form. Adjust quantity and scan next.`,
            });
          }
        } else {
          setSearchStatus("Not found. Please enter name manually.");
          setDataSource(null);
          toast({
            title: "New Item Detected! 📌",
            description:
              "We don't know this one yet. Quick Add it to help the app get smarter!",
          });

          if (!isMultiScan) {
            setIsScanning(false);
            setTimeout(() => {
              const nameInput = document.querySelector(
                '[data-testid="input-product-name"]',
              ) as HTMLInputElement;
              if (nameInput) nameInput.focus();
            }, 300);
            html5QrCode
              ?.stop()
              .catch((err) => console.error("Error stopping scanner", err));
          }
        }
      } catch (err) {
        console.error("Lookup error:", err);
        setSearchStatus("Search failed.");
        if (!isMultiScan) {
          setIsScanning(false);
          html5QrCode
            ?.stop()
            .catch((err) => console.error("Error stopping scanner", err));
        }
      } finally {
        setIsSearching(false);
      }
    };

    if (isScanning && open) {
      html5QrCode = new Html5Qrcode("barcode-reader");
      const config = { fps: 10, qrbox: { width: 250, height: 250 } };

      html5QrCode
        .start({ facingMode: "environment" }, config, handleScan, (error) => {
        })
        .catch((err) => {
          console.error("Camera start error:", err);
          setIsScanning(false);
        });
    }
    return () => {
      if (html5QrCode?.isScanning) {
        html5QrCode.stop().catch((err) => console.error("Cleanup error", err));
      }
    };
  }, [isScanning, open, form]);

  const handleSubmit = (data: ProductFormValues) => {
    const isFromExternal =
      dataSource === "external" || dataSource === "firebase" || !!data.barcode;
    onSubmit({ ...data, isFromExternal } as any);
    form.reset();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px] max-h-[90vh] overflow-y-auto rounded-[32px] p-6 bg-[#FFFDF5] dark:bg-slate-900 border-none shadow-2xl">
        {isSearching && (
          <div className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-black/80 backdrop-blur-sm transition-all duration-300">
            <div className="relative">
              <div
                className="h-24 w-24 rounded-full border-t-4 border-b-4 animate-spin"
                style={{ borderColor: "#8B1A1A" }}
              ></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <Barcode
                  className="h-10 w-10 animate-pulse"
                  style={{ color: "#8B1A1A" }}
                />
              </div>
            </div>
            <h2 className="mt-8 text-2xl font-bold text-white tracking-tight animate-bounce">
              Finding Product...
            </h2>
            <p className="mt-2 text-white/80 font-medium px-6 text-center">
              {searchStatus}
            </p>
            <div className="mt-6 flex gap-1">
              <div
                className="h-2 w-2 rounded-full animate-bounce [animation-delay:-0.3s]"
                style={{ backgroundColor: "#8B1A1A" }}
              ></div>
              <div
                className="h-2 w-2 rounded-full animate-bounce [animation-delay:-0.15s]"
                style={{ backgroundColor: "#8B1A1A" }}
              ></div>
              <div
                className="h-2 w-2 rounded-full animate-bounce"
                style={{ backgroundColor: "#8B1A1A" }}
              ></div>
            </div>
          </div>
        )}
        <DialogHeader>
          <DialogTitle className="font-bold text-slate-800 dark:text-white text-xl">
            {editProduct ? "Edit Product" : "Add New Product"}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="space-y-4"
          >
            <FormField
              control={form.control}
              name="productType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-xs font-bold text-slate-400 uppercase ml-1">
                    Product Type
                  </FormLabel>
                  <div className="flex rounded-2xl overflow-hidden border border-slate-100">
                    <button
                      type="button"
                      className={`flex-1 py-2.5 text-sm font-bold transition-colors ${field.value === "sale"
                        ? "text-white"
                        : "bg-slate-50 text-slate-600"
                        }`}
                      style={
                        field.value === "sale"
                          ? {
                            background:
                              "linear-gradient(135deg, #8B1A1A 0%, #631212 100%)",
                          }
                          : {}
                      }
                      onClick={() => field.onChange("sale")}
                    >
                      For Sale
                    </button>
                    <button
                      type="button"
                      className={`flex-1 py-2.5 text-sm font-bold transition-colors border-x border-slate-100 ${field.value === "expense"
                        ? "text-white"
                        : "bg-slate-50 text-slate-600"
                        }`}
                      style={
                        field.value === "expense"
                          ? {
                            background:
                              "linear-gradient(135deg, #8B1A1A 0%, #631212 100%)",
                          }
                          : {}
                      }
                      onClick={() => field.onChange("expense")}
                    >
                      For Expense
                    </button>
                    <button
                      type="button"
                      className={`flex-1 py-2.5 text-sm font-bold transition-colors ${field.value === "both"
                        ? "text-white"
                        : "bg-slate-50 text-slate-600"
                        }`}
                      style={
                        field.value === "both"
                          ? {
                            background:
                              "linear-gradient(135deg, #8B1A1A 0%, #631212 100%)",
                          }
                          : {}
                      }
                      onClick={() => field.onChange("both")}
                    >
                      Both
                    </button>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            {editProduct && (
              <div className="flex gap-2 items-end">
                <FormField
                  control={form.control}
                  name="barcode"
                  render={({ field }) => (
                    <FormItem className="flex-1">
                      <FormLabel className="text-xs font-bold text-slate-400 uppercase ml-1">
                        Barcode
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Scan or enter barcode"
                          className="h-13 rounded-2xl font-bold bg-slate-100 border-slate-100 text-slate-500 cursor-not-allowed"
                          {...field}
                          readOnly
                          data-testid="input-barcode"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}

            {isScanning && (
              <div className="space-y-4">
                <div className="flex items-center justify-between px-2 py-1">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="multi-scan"
                      checked={isMultiScan}
                      onChange={(e) => setIsMultiScan(e.target.checked)}
                      className="h-4 w-4 rounded border-slate-200 accent-[#8B1A1A]"
                    />
                    <label
                      htmlFor="multi-scan"
                      className="text-sm cursor-pointer font-bold text-slate-600"
                    >
                      Continuous Scan Mode (Auto-add)
                    </label>
                  </div>
                </div>

                <div className="relative w-full h-48 overflow-hidden rounded-2xl border border-slate-100 bg-black shadow-inner">
                  <div id="barcode-reader" className="h-full w-full" />
                  <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                    <div className="relative h-28 w-56 border-2 border-white/50 rounded-sm overflow-hidden">
                      <div className="absolute top-0 left-0 w-full h-0.5 bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.8)] animate-[scan_2s_linear_infinite]" />
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-2xl border border-slate-100">
                  <div className="flex flex-col">
                    <span className="text-sm font-bold text-slate-700">
                      Quantity
                    </span>
                    <span className="text-xs text-slate-400 italic">
                      Update for next item
                    </span>
                  </div>
                  <div className="flex items-center gap-4">
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="h-10 w-10 rounded-full border-2 border-slate-200 transition-all active:scale-95"
                      onClick={() =>
                        setScannedQuantity((prev) => Math.max(1, prev - 1))
                      }
                    >
                      <span className="text-xl font-bold text-slate-600">
                        -
                      </span>
                    </Button>
                    <span className="text-2xl font-black min-w-[30px] text-center text-slate-800">
                      {scannedQuantity}
                    </span>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="h-10 w-10 rounded-full border-2 border-slate-200 transition-all active:scale-95"
                      onClick={() => setScannedQuantity((prev) => prev + 1)}
                    >
                      <span className="text-xl font-bold text-slate-600">
                        +
                      </span>
                    </Button>
                  </div>
                </div>

                <style>{`
                  @keyframes scan {
                    0%, 100% { top: 0%; }
                    50% { top: 100%; }
                  }
                  #barcode-reader video {
                    object-fit: cover !important;
                    height: 100% !important;
                  }
                `}</style>
              </div>
            )}

            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-xs font-bold text-slate-400 uppercase ml-1">
                    Product Name
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Enter product name"
                      className="h-13 rounded-2xl font-bold bg-slate-50 border-slate-100 text-slate-900"
                      {...field}
                      data-testid="input-product-name"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              {(productType === "sale" || productType === "both") && (
                <FormField
                  control={form.control}
                  name="salePrice"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs font-bold text-slate-400 uppercase ml-1">
                        Sale Price (Rs.)
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="0"
                          className="h-13 rounded-2xl font-bold bg-slate-50 border-slate-100 text-slate-900"
                          {...field}
                          data-testid="input-sale-price"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {(productType === "expense" || productType === "both") && (
                <FormField
                  control={form.control}
                  name="costPrice"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs font-bold text-slate-400 uppercase ml-1">
                        Cost Price (Rs.)
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="0"
                          className="h-13 rounded-2xl font-bold bg-slate-50 border-slate-100 text-slate-900"
                          {...field}
                          data-testid="input-cost-price"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </div>

            <FormField
              control={form.control}
              name="category"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-xs font-bold text-slate-400 uppercase ml-1">
                    Category{" "}
                    <span className="text-red-500">*</span>
                  </FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    value={field.value || "General"}
                  >
                    <FormControl>
                      <SelectTrigger
                        className="h-13 rounded-2xl font-bold bg-slate-50 border-slate-100 text-slate-900"
                        data-testid="select-product-category"
                      >
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent className="rounded-2xl">
                      {displayCategories.map((cat) => (
                        <SelectItem key={cat} value={cat}>
                          {cat}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="color"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-xs font-bold text-slate-400 uppercase ml-1">
                    Color
                  </FormLabel>
                  <div className="flex gap-2 flex-wrap">
                    {PRODUCT_COLORS.map((color) => (
                      <button
                        key={color}
                        type="button"
                        className={`w-8 h-8 rounded-full border-2 transition-all ${field.value === color
                          ? "border-[#8B1A1A] scale-110 shadow-sm"
                          : "border-transparent"
                          }`}
                        style={{ backgroundColor: color }}
                        onClick={() => field.onChange(color)}
                      />
                    ))}
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            {userMode === "pro" && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="gstRate"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-xs font-bold text-slate-400 uppercase ml-1">
                          GST Rate (%){" "}
                          <span className="text-red-500">*</span>
                        </FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            placeholder="0"
                            className="h-13 rounded-2xl font-bold bg-slate-50 border-slate-100 text-slate-900"
                            {...field}
                            data-testid="input-gst-rate"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="taxType"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-xs font-bold text-slate-400 uppercase ml-1">
                          Tax Type{" "}
                          <span className="text-red-500">*</span>
                        </FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <FormControl>
                            <SelectTrigger
                              className="h-13 rounded-2xl font-bold bg-slate-50 border-slate-100 text-slate-900"
                              data-testid="select-tax-type"
                            >
                              <SelectValue placeholder="Select tax type" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent className="rounded-2xl">
                            <SelectItem value="none">None</SelectItem>
                            <SelectItem value="gst">GST</SelectItem>
                            <SelectItem value="igst">IGST</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <FormField
                  control={form.control}
                  name="hsn"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs font-bold text-slate-400 uppercase ml-1">
                        HSN Code{" "}
                        <span className="text-red-500">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Required"
                          className="h-13 rounded-2xl font-bold bg-slate-50 border-slate-100 text-slate-900"
                          {...field}
                          data-testid="input-hsn"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <a
                  href="https://cbic-gst.gov.in/gst-goods-services-rates.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs font-semibold ml-1"
                  style={{ color: "#8B1A1A" }}
                >
                  🔍 Find HSN Code &amp; GST Rate →
                </a>

                <div className="flex items-center justify-between p-4 bg-slate-50 border border-slate-100 rounded-2xl">
                  <FormField
                    control={form.control}
                    name="gstInclusive"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center justify-between w-full space-y-0">
                        <FormLabel className="text-sm font-bold text-slate-600">
                          {field.value ? "Price Including GST" : "Price Excluding GST"}
                        </FormLabel>
                        <FormControl>
                          <div
                            className={`w-12 h-6 flex items-center rounded-full p-1 cursor-pointer transition-colors`}
                            style={{
                              backgroundColor: field.value
                                ? "#8B1A1A"
                                : "#e2e8f0",
                            }}
                            onClick={() => field.onChange(!field.value)}
                            data-testid="toggle-gst-inclusive"
                          >
                            <div
                              className={`bg-white w-4 h-4 rounded-full shadow-md transform transition-transform ${field.value ? "translate-x-6" : "translate-x-0"
                                }`}
                            />
                          </div>
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="stock"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-xs font-bold text-slate-400 uppercase ml-1">
                          Current Stock
                        </FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            placeholder="0"
                            className="h-13 rounded-2xl font-bold bg-slate-50 border-slate-100 text-slate-900"
                            {...field}
                            data-testid="input-stock"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="lowStockAlert"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-xs font-bold text-slate-400 uppercase ml-1">
                          Low Stock Alert
                        </FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            placeholder="5"
                            className="h-13 rounded-2xl font-bold bg-slate-50 border-slate-100 text-slate-900"
                            {...field}
                            data-testid="input-low-stock-alert"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </>
            )}

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="ghost"
                className="flex-1 h-14 rounded-2xl font-bold text-slate-400 uppercase"
                onClick={() => {
                  setIsScanning(false);
                  onOpenChange(false);
                }}
              >
                CANCEL
              </Button>
              <Button
                type="submit"
                className="flex-[1.5] h-14 rounded-2xl font-bold text-white border-none"
                style={{
                  background:
                    "linear-gradient(135deg, #8B1A1A 0%, #631212 100%)",
                }}
                disabled={isLoading}
                data-testid="button-save-product"
              >
                {isLoading
                  ? "Saving..."
                  : editProduct
                    ? "UPDATE"
                    : "ADD PRODUCT"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}